import { memo, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';

import { sendPostMessage } from '../lib/sendPostMessage';
import {
  type Gds_Event,
  isWwsWeddingWebsiteV2,
  type WeddingWebsiteQuery,
  type Wws_Page,
} from '../types/graphql';
import { RegistryProvider } from '../context/RegistryContext';
import Seo from './Seo';
import { TrackingProvider } from './TrackingContext';
import { WWContext, type WWContextValue } from './WWContext';
import PreloadImages from './PreloadImages';
import { ParsedUrlQuery } from 'querystring';
import TemplateWrapper from './TemplateWrapper';
import { InlineEditProvider } from '../context/InlineEditContext';
import { useBuildRouteFunction } from '../lib/hooks/useRouteBuilder';
import Cookies from 'universal-cookie';

const defaultEvents: Gds_Event[] = [];

declare global {
  interface Window {
    dataLayer: GuestViewDL;
    webkit?: {
      messageHandlers: {
        [x: string]: {
          postMessage: (data: string) => void;
        };
      };
    };
  }
}

interface GuestViewDL {
  push: (data: GuestViewData) => void;
}

interface GuestViewData {
  event: string;
  isCustomDomain: boolean;
}

const getRouteName = (query: ParsedUrlQuery) => {
  if (Array.isArray(query['route'])) {
    return query['route'][0];
  }

  return query['route'] || '';
};

const isPageRequestedHidden = (
  data: WeddingWebsiteQuery,
  routeName: string
): boolean => {
  // if no route requested, then not hidden
  const phantomPages = [
    'home',
    'rsvp',
    'rsvp-section',
    'registry',
    'registry-section',
  ];
  if (!routeName || phantomPages.includes(routeName)) {
    return false;
  }

  if (isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    const requestedPage = data.weddingWebsiteResult.pages?.find(
      (page: Wws_Page): boolean => page.routeName === routeName
    );

    const hiddenPage = !requestedPage?.show;

    return Boolean(hiddenPage);
  }
  return false;
};

const isOwner = (query: ParsedUrlQuery): boolean => {
  // without making an extra membership query call we check if it's preview mode
  // and has xo-session token to determine if owner
  const isPreview = query['preview'] === 'true';
  const cookies = new Cookies();
  const hasSessionToken = Boolean(cookies.get('xo-session-token'));
  return Boolean(isPreview && hasSessionToken);
};

function Main() {
  const { query, replace, reload } = useRouter();
  const routeName = getRouteName(query);
  const { buildRoute } = useBuildRouteFunction();

  const { data, setWWData } = useContext<WWContextValue>(WWContext);

  useEffect(() => {
    try {
      // This dataLayer array should be loaded at this point, but adding to make sure is available to use.
      window.dataLayer = window.dataLayer || [];

      const isCustomDomain = !window.location.host.includes('theknot.com');
      const guestViewDL: GuestViewData = {
        event: 'WWSCustomDomain',
        isCustomDomain,
      };

      window.dataLayer.push(guestViewDL);
    } catch {
      console.error('Issues pushing custom domain property to GTM data layer');
    }
  }, []);

  useEffect((): void => {
    // this ignored customPage types so we set it in the handler function
    // setting it in useEffect creates in infinite loop
    const currentPage = isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)
      ? data?.weddingWebsiteResult.pages?.find((page: Wws_Page): boolean => {
          return page.routeName === routeName;
        })
      : undefined;

    if (!currentPage && routeName === 'rsvp') {
      sendPostMessage({ type: 'currentPage', data: 'rsvp' });
    } else {
      sendPostMessage({ type: 'currentPage', data: currentPage });
    }
  }, [data?.weddingWebsiteResult, routeName]);

  useEffect((): (() => void) => {
    const handler = (event: MessageEvent): void | Promise<boolean> => {
      if (
        event.source !== window ||
        window?.webkit // Allows iOS to bypass origin check
      ) {
        try {
          const message = JSON.parse(event.data);

          switch (message.type) {
            case 'currentPage': {
              return replace(buildRoute(message.data.routeName));
            }

            case 'updatePage': {
              const wwData: WeddingWebsiteQuery = {
                ...(data as WeddingWebsiteQuery),
                events: data?.events || defaultEvents,
                weddingWebsiteResult:
                  data?.weddingWebsiteResult &&
                  isWwsWeddingWebsiteV2(data.weddingWebsiteResult)
                    ? {
                        ...data.weddingWebsiteResult,
                        coverPhoto: {
                          ...data.weddingWebsiteResult.coverPhoto,
                          ...message.data.coverPhoto,
                        },
                        coverPhotoPathWithCropbox:
                          message.data.coverPhotoPathWithCropbox ||
                          data.weddingWebsiteResult.coverPhotoPathWithCropbox,

                        pages:
                          data.weddingWebsiteResult?.pages?.map(
                            (page: Wws_Page): Wws_Page =>
                              page.id === message.data.id ? message.data : page
                          ) || [],
                      }
                    : null,
              };

              // let parent know custom page has changed.
              if (message.data.type === 'CustomPage') {
                sendPostMessage({ type: 'currentPage', data: message.data });
              }

              return setWWData(wwData);
            }

            case 'updateRegistryNote': {
              const wwData: WeddingWebsiteQuery = {
                ...(data as WeddingWebsiteQuery),
                events: data?.events || defaultEvents,
                weddingWebsiteResult:
                  data?.weddingWebsiteResult &&
                  isWwsWeddingWebsiteV2(data.weddingWebsiteResult)
                    ? {
                        ...data.weddingWebsiteResult,
                        registryNote: message.data,
                      }
                    : null,
              };

              // If anything else on weddingWebsiteQuery needs to be update create a case for that
              return setWWData(wwData);
            }

            case 'updateBasicInfo': {
              const wwData: WeddingWebsiteQuery = {
                ...(data as WeddingWebsiteQuery),
                events: data?.events || defaultEvents,
                weddingWebsiteResult: {
                  ...data?.weddingWebsiteResult,
                  ...message?.data?.weddingWebsiteResult,
                },
                weddingDatePreferences: {
                  ...data?.weddingDatePreferences,
                  ...message?.data?.weddingDatePreferences,
                },
              };

              return setWWData(wwData);
            }

            case 'reload':
              reload();

              return;
          }
        } catch {
          // Ignore the error?
        }
      }
    };

    window.addEventListener('message', handler);

    return (): void => {
      window.removeEventListener('message', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    // redirect page if page is hidden and not owner
    if (!data || !routeName) return;

    const shouldRedirect =
      isPageRequestedHidden(data, routeName) && !isOwner(query);

    if (shouldRedirect) {
      const redirectRoute = buildRoute('');
      replace(redirectRoute);
    }
  }, [data, query, routeName, replace, buildRoute]);

  return data ? (
    <>
      <Seo data={data} />
      <PreloadImages data={data} />
      <RegistryProvider data={data}>
        <TrackingProvider data={data}>
          <InlineEditProvider isInEditMode={false}>
            <TemplateWrapper />
          </InlineEditProvider>
        </TrackingProvider>
      </RegistryProvider>
    </>
  ) : null;
}

export default memo(Main);
